'use client';

import Axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios';
import { message } from 'antd';
import { getCurrentToken } from '@/lib/utils/cookies';

// Types
export interface RequestOptions<T = any> extends AxiosRequestConfig {
  endpoint?: string;
  parser?: {
    parse: (data: any) => Promise<T> | T;
  };
  passthroughErrorCatcher?: boolean;
}

export type ServiceOptions = {
  endpoint: string;
};

export type RefreshTokenInvoker = () => Promise<void>;

export interface Notifier {
  error: (msg: string) => void;
  warn: (msg: string) => void;
}

// URL join utility (simpler than installing url-join package)
function urlJoin(...parts: string[]): string {
  return parts
    .map((part, index) => {
      if (index === 0) {
        return part.trim().replace(/\/+$/, '');
      }
      return part.trim().replace(/(^\/+|\/+$)/g, '');
    })
    .filter((part) => part.length > 0)
    .join('/');
}

/**
 * Base service class for all API communication
 * Features:
 * - Token management (static token + cookies)
 * - Automatic token refresh with queue mechanism
 * - Retry logic (5 attempts with 3s delay)
 * - Error handling (code 20003 = token expired)
 * - Axios interceptors
 */
export abstract class ServiceBase {
  private static token?: string;
  private static baseUrl: string;
  private static notifier: Notifier;
  private static refreshTokenInvoking = false;
  private static refreshTokenInvoker: RefreshTokenInvoker | undefined;
  private static refreshTokenQueue: {
    resolver: VoidFunction;
    rejector: VoidFunction;
  }[] = [];

  /**
   * Configure base URL and notifier for all services
   */
  static setConfig(baseUrl: string, n: Notifier) {
    ServiceBase.baseUrl = baseUrl;
    ServiceBase.notifier = n;
  }

  /**
   * Set static token for all requests
   */
  static setToken(token?: string) {
    ServiceBase.token = token;
  }

  /**
   * Set token refresh function with retry logic (5 attempts, 3s delay)
   */
  static setTokenRefresher(invoker: RefreshTokenInvoker) {
    ServiceBase.refreshTokenInvoker = async () => {
      if (ServiceBase.refreshTokenInvoking) {
        return;
      }
      ServiceBase.refreshTokenInvoking = true;
      try {
        try {
          await invoker();
        } catch {
          // Retry 5 times with 3s delay
          const isOk = await [1, 2, 3, 4, 5].reduce(async (acc: Promise<boolean>) => {
            const lastOk = await acc;
            if (lastOk) return true;
            return new Promise<boolean>((r) => {
              setTimeout(async () => {
                try {
                  await invoker();
                  r(true);
                } catch {
                  r(false);
                }
              }, 3000);
            });
          }, Promise.resolve(false));
          if (!isOk) throw new Error('Token refresh failed');
        }
        // Resolve all queued requests
        ServiceBase.refreshTokenQueue.map(({ resolver }) => resolver());
      } catch {
        // Reject all queued requests
        ServiceBase.refreshTokenQueue.map(({ rejector }) => rejector());
      } finally {
        ServiceBase.refreshTokenQueue.splice(0, ServiceBase.refreshTokenQueue.length);
        ServiceBase.refreshTokenInvoking = false;
      }
    };
  }

  /**
   * Queue-based token refresh (prevents concurrent refresh calls)
   */
  private static refreshToken() {
    return new Promise<void>((resolver, rejector) => {
      ServiceBase.refreshTokenQueue.push({
        resolver,
        rejector,
      });
      if (!ServiceBase.refreshTokenInvoking) {
        if (ServiceBase.refreshTokenInvoker) {
          ServiceBase.refreshTokenInvoker();
        } else {
          rejector();
          ServiceBase.refreshTokenQueue.splice(0, ServiceBase.refreshTokenQueue.length);
        }
      }
    });
  }

  protected options: ServiceOptions = { endpoint: '' };
  private client: AxiosInstance;

  constructor(options?: ServiceOptions) {
    this.options = { endpoint: '', ...options };

    this.client = Axios.create({
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      timeout: 300000, // 5 minutes
      transformRequest: [
        (data, headers) => {
          if (data instanceof FormData) {
            if (headers) {
              delete headers['Content-Type'];
            }
            return data;
          }
          return JSON.stringify(data);
        },
      ],
    });

    // Response interceptor - normalize errors
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        return Promise.reject(error.response ? error.response.data : error);
      },
    );
  }

  /**
   * Error handler
   * Returns true if request should be retried, false to throw
   */
  private async catchError(e: unknown): Promise<boolean> {
    if (typeof e === 'object' && e) {
      const { code, message: errorMessage } = e as any;
      if (code) {
        // Backend error with code
        if (code === 20003) {
          // Token expired
          try {
            await ServiceBase.refreshToken();
            return true; // Retry request
          } catch {
            return false; // Failed to refresh, throw error
          }
        }
        if (errorMessage) {
          console.log(errorMessage);
        }
        return false;
      }
    }
    if (e instanceof Error) {
      if (e.message === 'Network Error') {
        ServiceBase.notifier.error('Máy chủ đang bận hoặc đang bảo trì, vui lòng quay lại sau');
        return false;
      }
    }
    console.log('Xảy ra một lỗi chưa xác định, vui lòng thử lại hoặc liên hệ bộ phận hỗ trợ');
    return false;
  }

  /**
   * Get request options with token injection
   */
  private getOptions(options?: RequestOptions) {
    let headers = options?.headers;
    const token = getCurrentToken();

    if (headers && (headers as any).Authorization) {
      // Keep explicit token if provided
    } else if (ServiceBase.token || token) {
      // Use static token or cookie token
      headers = {
        ...headers,
        Authorization: ServiceBase.token || token,
      };
    }
    return { ...options, headers };
  }

  /**
   * Get absolute URL for request
   */
  private getAbsoluteRequestUrl(options?: RequestOptions) {
    if (options?.endpoint?.startsWith('http')) {
      return options.endpoint;
    }
    if (this.options.endpoint.startsWith('http')) {
      return urlJoin(this.options.endpoint, options?.endpoint || '');
    }
    return urlJoin(ServiceBase.baseUrl, this.options.endpoint, options?.endpoint || '');
  }

  /**
   * GET request with automatic retry on token expiry
   */
  async get<T = any>(options?: RequestOptions): Promise<T> {
    const opts = this.getOptions(options);
    try {
      const { data } = await this.client.get(this.getAbsoluteRequestUrl(options), opts);
      if (opts.parser) {
        return await opts.parser.parse(data);
      }
      return data.data;
    } catch (e) {
      if (opts.passthroughErrorCatcher) throw e;
      const catched = await this.catchError(e);
      if (catched) {
        return this.get(options);
      }
      throw e;
    }
  }

  /**
   * POST request with automatic retry on token expiry
   */
  async post<T = any>(data: any, options?: RequestOptions): Promise<T> {
    const opts = this.getOptions(options);
    try {
      const { data: result } = await this.client.post(this.getAbsoluteRequestUrl(options), data, opts);
      if (opts.parser) {
        return await opts.parser.parse(result);
      }
      return result.data;
    } catch (e) {
      if (opts.passthroughErrorCatcher) throw e;
      const catched = await this.catchError(e);
      if (catched) {
        return this.post(data, options);
      }
      throw e;
    }
  }

  /**
   * PATCH request with automatic retry on token expiry
   */
  async patch<T = any>(data: any, options?: RequestOptions): Promise<T> {
    const opts = this.getOptions(options);
    try {
      const { data: result } = await this.client.patch(this.getAbsoluteRequestUrl(options), data, opts);
      if (opts.parser) {
        return await opts.parser.parse(result);
      }
      return result.data;
    } catch (e) {
      if (opts.passthroughErrorCatcher) throw e;
      const catched = await this.catchError(e);
      if (catched) {
        return this.patch(data, options);
      }
      throw e;
    }
  }

  /**
   * DELETE request with automatic retry on token expiry
   */
  async delete<T = any>(options?: RequestOptions): Promise<T> {
    const opts = this.getOptions(options);
    try {
      const { data } = await this.client.delete(this.getAbsoluteRequestUrl(options), opts);
      if (opts.parser) {
        return await opts.parser.parse(data);
      }
      return data.data;
    } catch (e) {
      if (opts.passthroughErrorCatcher) throw e;
      const catched = await this.catchError(e);
      if (catched) {
        return this.delete(options);
      }
      throw e;
    }
  }
}
